import { useState, useEffect } from "react";
import Icon from "./Icon";

const WEATHER_API_KEY = process.env.REACT_APP_OPENWEATHER_API_KEY;

const weatherEmoji = (code) => {
  if (code >= 200 && code < 300) return "⛈";
  if (code >= 300 && code < 400) return "🌦";
  if (code >= 500 && code < 600) return "🌧";
  if (code >= 600 && code < 700) return "❄️";
  if (code >= 700 && code < 800) return "🌫";
  if (code === 800) return "☀️";
  if (code === 801) return "🌤";
  if (code <= 804) return "☁️";
  return "🌡";
};

export function extractCityFromAddress(address) {
  if (!address) return null;

  const COUNTRIES = /^(United States|USA|US|United Kingdom|UK|France|Germany|Italy|Spain|Australia|Canada|Mexico|Japan|China|India|Brazil|Netherlands|Belgium|Portugal|Greece|Switzerland|Austria|Sweden|Norway|Denmark|Finland|Poland|Czech Republic|Hungary|Romania|Bulgaria|Croatia|Serbia|Turkey|Israel|UAE|South Africa|New Zealand|Argentina|Colombia|Chile|Peru|Russia|Thailand|Vietnam|Singapore|Malaysia|Indonesia|Philippines|South Korea|Taiwan)$/i;
  const STATE_CODE = /^[A-Z]{2}(\s+\d{5}(-\d{4})?)?$/;

  const parts = address.split(',').map(p => p.trim()).filter(Boolean);

  if (parts.length >= 2) {
    // Pass 1: find the part immediately before a state/province code
    // e.g. "Chicago, IL 60601" → "Chicago" is right before "IL 60601"
    for (let i = 0; i < parts.length - 1; i++) {
      const next = parts[i + 1];
      if (STATE_CODE.test(next) || /^[A-Z]{2}\s+\d{5}/.test(next)) {
        const part = parts[i];
        if (!/^\d/.test(part) && !/\s+\d+$/.test(part) && part.length >= 2 && part.length <= 40) {
          return cleanCityResult(part);
        }
      }
    }

    // Pass 2: scan for zip-prefixed city or best candidate
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      // "84410 Crillon-le-Brave" or "00193 Roma" or "1200-205 Lisboa"
      const zipThenCity = part.match(/^\d{4,6}(?:-\d+)?\s+([A-Za-zÀ-ÿ].+)/);
      if (zipThenCity) return cleanCityResult(zipThenCity[1].trim());

      if (COUNTRIES.test(part)) continue;
      if (STATE_CODE.test(part)) continue;
      if (/^\d{4,6}(-\d{4})?$/.test(part)) continue; // bare zip
      if (/^[A-Z]{2}\s+\d{5}/.test(part)) continue;
      if (part.length < 2 || part.length > 50) continue;
      if (/^\d+\s+\w/.test(part)) continue;  // starts with house number
      if (/\s+\d+$/.test(part)) continue;    // ends with number (street + house no.)
      if (/^\d+$/.test(part)) continue;      // just a number

      // Skip street-type openers if a better candidate follows
      const looksLikeStreet = /^(Via|Viale|Corso|Largo|Vicolo|Borgo|Rue|Avenue|Boulevard|Place|Piazza|Plaza|Rua|Calle|Strasse|Straße|Quai|Avenida|Alameda)\b/i.test(part);
      if (looksLikeStreet) {
        const laterZipCity = parts.slice(i + 1).some(p => /^\d{4,6}(?:-\d+)?\s+[A-Za-zÀ-ÿ]/.test(p));
        if (laterZipCity) continue;
        const laterCity = parts.slice(i + 1).find(p =>
          !COUNTRIES.test(p) && !STATE_CODE.test(p) &&
          !/^\d/.test(p) && p.length >= 2 && p.length <= 40 &&
          !/\s+\d+$/.test(p) && !/^\d+$/.test(p)
        );
        if (laterCity) continue;
      }

      return cleanCityResult(part);
    }
  }

  // --- No-comma fallback: city appears immediately before the postal code ---
  // e.g. "10 rue Agrippa d'Aubigné Paris 75004 FR" → Paris
  // e.g. "8 avenue de la Violette Aix-en-Provence 13100 FR" → Aix-en-Provence
  let cleaned = address
    .replace(/,?\s*(United States|USA|US|United Kingdom|UK|France|Germany|Italy|Spain|Australia|Canada|Mexico|Japan|Portugal|Greece|Switzerland|Austria|Sweden|Norway|Denmark|Finland|Poland|Romania|Bulgaria|Croatia|Serbia|Turkey|Israel|UAE|South Africa|New Zealand|Russia|Thailand|Vietnam|Singapore|Malaysia|Indonesia|Philippines)$/i, '')
    .replace(/,?\s*\b[A-Z]{2}\b\s*$/, '')
    .trim();

  const cityBeforeZip = cleaned.match(/([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ'-]*(?:[- ][A-Za-zÀ-ÿ][A-Za-zÀ-ÿ'-]*)*)\s+\d{4,6}\s*$/);
  if (cityBeforeZip) {
    const candidate = cityBeforeZip[1].trim();
    // Walk right-to-left collecting only the final run of capitalized words.
    // This handles "de la Violette Aix-en-Provence" → stops at lowercase "Violette"... wait,
    // "Violette" IS capitalized. Real fix: take only the LAST word if it starts with a capital,
    // then continue leftward only while words are clearly part of a multi-word city (e.g. "New York").
    // Heuristic: a word is part of the city name if it starts with a capital AND the word to its
    // right is also capitalised. Stop the moment adding another word produces a string that looks
    // like "StreetWord CityName" (i.e., the leftmost word is a standalone capitalised noun that
    // isn't a directional or known article).
    const words = candidate.split(/\s+/);
    const cityWords = [];
    for (let i = words.length - 1; i >= 0; i--) {
      const w = words[i];
      if (/^[A-ZÀ-Ÿ]/.test(w)) {
        cityWords.unshift(w);
        // Stop after collecting one hyphenated city name token (e.g. "Aix-en-Provence")
        // unless the next word to the left is also capitalised and short (e.g. "New York")
        if (cityWords.length >= 1 && i > 0) {
          const prev = words[i - 1];
          // Continue only if prev is also capitalised (multi-word city like "New York", "San Francisco")
          if (!/^[A-ZÀ-Ÿ]/.test(prev)) break;
          // But stop if prev looks like a street/nature word that happens to be capitalised
          // (e.g. "Violette" before "Aix-en-Provence")
          // Simple heuristic: if the hyphenated city name already covers a known pattern, stop
          if (w.includes('-')) break; // "Aix-en-Provence" is clearly a complete city name
        }
      } else {
        break;
      }
    }
    return cleanCityResult(cityWords.length > 0 ? cityWords.join(' ') : candidate);
  }

  return null;
}

function cleanCityResult(city) {
  if (!city) return city;
  // Remove trailing postal/district codes: "London SW1X 7AT" → "London", "Firenze FI" → "Firenze"
  city = city.replace(/\s+[A-Z]{1,2}[\dA-Z]+(?:\s+[\dA-Z]+)*$/, '').trim();
  // Remove leading number remnant: "205 Lisboa" → "Lisboa"
  city = city.replace(/^\d+\s+/, '').trim();
  return city || null;
}

// cityName: "City" or "City,CC" fallback query
// displayName: label to show in the header (OpenWeatherMap's own name can be wrong, e.g. "Arrondissement de Nice")
// lat/lng: optional; when present the weather is looked up by coordinates instead of by name
export default function WeatherModal({ cityName, displayName, lat, lng, onClose }) {
  const [weather, setWeather] = useState(null);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manualCity, setManualCity] = useState("");
  const [currentCity, setCurrentCity] = useState(cityName);
  const [coords, setCoords] = useState(
    typeof lat === "number" && typeof lng === "number" ? { lat, lng } : null
  );
  const [label, setLabel] = useState(displayName || null);
  const [expandedDay, setExpandedDay] = useState(null);

  // Searching for a different city by name drops the coordinates and custom label
  const searchManualCity = () => {
    const value = manualCity.trim();
    if (!value) return;
    setCoords(null);
    setLabel(null);
    setCurrentCity(value);
    setManualCity("");
  };

  const fetchWeather = async (city, coordinates) => {
    if (!city && !coordinates) return;
    const locationParams = coordinates
      ? `lat=${coordinates.lat}&lon=${coordinates.lng}`
      : `q=${encodeURIComponent(city)}`;
    setLoading(true);
    setError(null);
    setWeather(null);
    setDays([]);
    try {
      if (!WEATHER_API_KEY) {
        setError("Weather API key not configured");
        setLoading(false);
        return;
      }
      const res = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?${locationParams}&appid=${WEATHER_API_KEY}&units=imperial`
      );
      const data = await res.json();
      if (data.cod !== 200) {
        setError(`"${city || label}" not found — try editing below`);
        setLoading(false);
        return;
      }
      setWeather(data);

      // Fetch full 5-day / 3-hour forecast
      const fRes = await fetch(
        `https://api.openweathermap.org/data/2.5/forecast?${locationParams}&appid=${WEATHER_API_KEY}&units=imperial&cnt=40`
      );
      const fData = await fRes.json();

      // Group by date
      const grouped = {};
      fData.list?.forEach(item => {
        const date = item.dt_txt.split(" ")[0];
        if (!grouped[date]) grouped[date] = [];
        grouped[date].push(item);
      });

      // Build day summaries with detail
      const dayList = Object.entries(grouped).slice(0, 5).map(([date, items]) => {
                const tempMax = Math.max(...items.map(i => i.main.temp_max));
        const tempMin = Math.min(...items.map(i => i.main.temp_min));
        const humidity = Math.round(items.reduce((s, i) => s + i.main.humidity, 0) / items.length);
        const windSpeed = Math.round(items.reduce((s, i) => s + i.wind.speed, 0) / items.length);
        const pop = Math.round(Math.max(...items.map(i => i.pop || 0)) * 100);

        // Noon item for main emoji/description
        const noon = items.find(i => i.dt_txt.includes("12:00")) || items[Math.floor(items.length / 2)];

        // Time slots: morning (6-10), afternoon (12-16), evening (18-22)
        const slots = [
          { label: "Morning", hours: ["06:00:00", "09:00:00"] },
          { label: "Afternoon", hours: ["12:00:00", "15:00:00"] },
          { label: "Evening", hours: ["18:00:00", "21:00:00"] },
        ].map(slot => {
          const item = items.find(i => slot.hours.includes(i.dt_txt.split(" ")[1]));
          return item ? {
            label: slot.label,
            temp: Math.round(item.main.temp),
            emoji: weatherEmoji(item.weather[0].id),
            desc: item.weather[0].description,
          } : null;
        }).filter(Boolean);

        return { date, tempMax: Math.round(tempMax), tempMin: Math.round(tempMin), humidity, windSpeed, pop, noon, slots };
      });

      setDays(dayList);
    } catch {
      setError("Couldn't load weather");
    }
    setLoading(false);
  };

  useEffect(() => { fetchWeather(currentCity, coords); }, [currentCity, coords]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDay = (dateStr) => {
    const d = new Date(dateStr + "T12:00:00");
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  const isToday = (dateStr) => dateStr === new Date().toISOString().split("T")[0];

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Weather</div>
            <div style={{ fontSize: 12, color: "var(--ink-light)" }}>
              {label || weather?.name || currentCity}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {loading && (
            <div style={{ textAlign: "center", padding: "24px 0", color: "var(--ink-light)", fontSize: 14 }}>
              Loading weather for {label || currentCity}...
            </div>
          )}

          {error && !loading && (
            <div style={{ fontSize: 13, color: "#C05621", marginBottom: 10, padding: "8px 10px", background: "#FEF3EC", borderRadius: 6 }}>
              {error}
            </div>
          )}

          {weather && !loading && (
            <>
              {/* Current conditions */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 0", borderBottom: "1px solid var(--border)", marginBottom: 12
              }}>
                <div>
                  <div style={{ fontSize: 48, lineHeight: 1 }}>{weatherEmoji(weather.weather[0].id)}</div>
                  <div style={{ fontSize: 13, color: "var(--ink-mid)", marginTop: 4, textTransform: "capitalize" }}>
                    {weather.weather[0].description}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--ink-light)", marginTop: 2 }}>
                    💧 {weather.main.humidity}% · 💨 {Math.round(weather.wind.speed)} mph
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 42, fontWeight: 300 }}>{Math.round(weather.main.temp)}°F</div>
                  <div style={{ fontSize: 12, color: "var(--ink-light)" }}>Feels like {Math.round(weather.main.feels_like)}°</div>
                  <div style={{ fontSize: 12, color: "var(--ink-light)" }}>H:{Math.round(weather.main.temp_max)}° L:{Math.round(weather.main.temp_min)}°</div>
                </div>
              </div>

              {/* 5-day forecast */}
              {days.map((day, i) => (
                <div key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                  {/* Day row — tap to expand */}
                  <div
                    onClick={() => setExpandedDay(expandedDay === i ? null : i)}
                    style={{ display: "flex", alignItems: "center", padding: "10px 0", cursor: "pointer", gap: 8 }}
                  >
                    <div style={{ fontSize: 13, color: "var(--ink-mid)", width: 110, flexShrink: 0 }}>
                      {isToday(day.date) ? "Today" : formatDay(day.date)}
                    </div>
                    <div style={{ fontSize: 18, flexShrink: 0 }}>{weatherEmoji(day.noon.weather[0].id)}</div>
                    <div style={{ flex: 1, fontSize: 12, color: "var(--ink-light)", textTransform: "capitalize" }}>
                      {day.noon.weather[0].description}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--ink-mid)", flexShrink: 0, textAlign: "right" }}>
                      <span style={{ fontWeight: 500 }}>{day.tempMax}°</span>
                      <span style={{ color: "var(--ink-faint)" }}> / {day.tempMin}°</span>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--ink-faint)", flexShrink: 0 }}>
                      {expandedDay === i ? "▲" : "▼"}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {expandedDay === i && (
                    <div style={{ padding: "0 0 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {/* Stats row */}
                      <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--ink-light)" }}>
                        {day.pop > 0 && <span>🌧 {day.pop}% rain</span>}
                        <span>💧 {day.humidity}% humidity</span>
                        <span>💨 {day.windSpeed} mph</span>
                      </div>
                      {/* Time slots */}
                      {day.slots.length > 0 && (
                        <div style={{ display: "flex", gap: 8 }}>
                          {day.slots.map((slot, j) => (
                            <div key={j} style={{
                              flex: 1, background: "var(--paper-warm)",
                              border: "1px solid var(--border)", borderRadius: 6,
                              padding: "8px", textAlign: "center"
                            }}>
                              <div style={{ fontSize: 11, color: "var(--ink-faint)", marginBottom: 4 }}>{slot.label}</div>
                              <div style={{ fontSize: 18 }}>{slot.emoji}</div>
                              <div style={{ fontSize: 13, fontWeight: 500 }}>{slot.temp}°</div>
                              <div style={{ fontSize: 10, color: "var(--ink-light)", textTransform: "capitalize", marginTop: 2 }}>{slot.desc}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        {/* Manual city input */}
        <div style={{ marginTop: 12, display: "flex", gap: 8, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
          <input
            className="form-input"
            placeholder="Try a different city..."
            value={manualCity}
            onChange={(e) => setManualCity(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") searchManualCity();
            }}
            style={{ flex: 1, fontSize: 13 }}
          />
          <button
            className="seg-btn primary"
            onClick={searchManualCity}
          >
            Go
          </button>
        </div>
      </div>
    </div>
  );
}